import { parseToArray } from '@/utils/common'

export const MAX_INPUT = 10
export const GLOBAL_QR_PREFIX = 'HTTPS://QR.NIKE.COM/'
export const GLOBAL_QR_LENGTH = 33

// 环节颜色与图标在亮 / 暗两种模式下保持不变（设计文档 5.4）
export const STAGE_CONFIG = {
  FACTORY: { color: '#722ed1', icon: 'factory' },
  DC: { color: '#1677ff', icon: 'dc', io: true },
  RSC: { color: '#13c2c2', icon: 'rsc', io: true },
  CSC: { color: '#8b5e34', icon: 'csc', io: true },
  CONSUMER: { color: '#52c41a', icon: 'consumer' },
  CONSUMER_SCAN: { color: '#237804', icon: 'cscan' },
  RETURN_2C: { color: '#fa8c16', icon: 'ret2c', io: true },
  TRANSHUB: { color: '#2f54eb', icon: 'transhub' },
  ADC: { color: '#eb2f96', icon: 'adc' },
  STORE: { color: '#7cb305', icon: 'store', store: true },
  RETURN_2B: { color: '#f5222d', icon: 'ret2b', io: true },
  UNKNOWN: { color: '#999999', icon: 'question' }
}

export const ACTION_CONFIG = {
  REWORK: { color: '#d48806', icon: 'rework', localeKey: 'rework', tag: 'gold' },
  REPRINT: { color: 'var(--epc-reprint)', icon: 'reprint', localeKey: 'reprint', tag: 'dark' }
}

export const IO_DIRS = ['IB', 'OB']
export const STORE_DIRS = ['IB', 'CYCLECOUNT', 'OB']

const LOOP_PAIRS = [
  ['CONSUMER', 'RETURN_2C'],
  ['STORE', 'RETURN_2B']
]

export const isGlobalQr = value => value.startsWith(GLOBAL_QR_PREFIX) && value.length === GLOBAL_QR_LENGTH

/**
 * 去空、转大写、去重，保持输入顺序
 */
export const parseInputValues = text => {
  const seen = new Set()
  return parseToArray(text)
    .map(v => v.toUpperCase())
    .filter(v => {
      if (seen.has(v)) return false
      seen.add(v)
      return true
    })
}

/**
 * 严格交替且至少回到起点一次（A→B→A…，长度 ≥ 3）才折叠为循环
 */
const isEvenIndex = (_, k) => k % 2 === 0
const isOddIndex = (_, k) => k % 2 === 1

export const groupStages = stages => {
  const groups = []
  for (let i = 0; i < stages.length; i++) {
    const pair = LOOP_PAIRS.find(p => p[0] === stages[i].stageType)
    if (pair) {
      let j = i
      while (j < stages.length && stages[j].stageType === pair[(j - i) % 2]) j++
      if (j - i >= 3) {
        const idx = []
        for (let k = i; k < j; k++) idx.push(k)
        groups.push({
          loop: true,
          pair,
          a: idx.filter(isEvenIndex),
          b: idx.filter(isOddIndex),
          first: i,
          last: j - 1
        })
        i = j - 1
        continue
      }
    }
    groups.push({ loop: false, idx: [i], first: i, last: i })
  }
  return groups
}

const toDate = value => (value ? new Date(value.replace(' ', 'T')) : null)

export const daysBetween = (from, to) => {
  const a = toDate(from)
  const b = toDate(to)
  if (!a || !b || isNaN(a) || isNaN(b)) return null
  return Math.max(0, Math.round((b - a) / 86400000))
}

export const fmt = (template, vars = {}) =>
  String(template || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : ''))

/**
 * 从当前语言的 epcCycleReport 文案中按路径取值，缺失时返回 fallback
 */
export const makeT = locale => (path, vars, fallback) => {
  const value = String(path)
    .split('.')
    .reduce((obj, key) => (obj && obj[key] != null ? obj[key] : undefined), locale)
  if (value == null || typeof value === 'object') return fallback != null ? fallback : path.split('.').pop()
  return vars ? fmt(value, vars) : value
}

const hexToRgb = hex => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '')
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null
}

/**
 * 原型中的 color-mix() 改为 rgba：按环节色生成几档透明度的 CSS 变量
 */
export const colorVars = color => {
  const rgb = hexToRgb(color)
  const a = alpha => (rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : 'rgba(128, 128, 128, ' + alpha + ')')
  return {
    '--c': color,
    '--c10': a(0.1),
    '--c25': a(0.25),
    '--c35': a(0.35),
    '--c45': a(0.45),
    '--c55': a(0.55),
    '--c70': a(0.7),
    '--c-dark': rgb
      ? `rgb(${Math.round(rgb[0] * 0.4 + 17 * 0.6)}, ${Math.round(rgb[1] * 0.4 + 17 * 0.6)}, ${Math.round(
          rgb[2] * 0.4 + 17 * 0.6
        )})`
      : '#222'
  }
}

export const stageColor = type => (STAGE_CONFIG[type] || {}).color || '#8c8c8c'

export const isIoStage = type => !!(STAGE_CONFIG[type] || {}).io
export const isStoreStage = type => !!(STAGE_CONFIG[type] || {}).store

export const dirsOf = (stages, list) => {
  const present = new Set(list.flatMap(n => stages[n].directions || []))
  const order = isStoreStage(stages[list[0]].stageType) ? STORE_DIRS : IO_DIRS
  return order.filter(d => present.has(d))
}

export const eventsOf = (stages, list, kind) =>
  list.flatMap(n => (stages[n].events || []).filter(ev => !kind || ev.kind === kind).map(ev => ({ n, ev })))

export const allIndexes = stages => stages.map((_, i) => i)

export const detailValue = (record, key) => {
  const d = (record.details || []).find(x => x.key === key)
  return d ? d.value : undefined
}

export const sortNewestFirst = list =>
  [...list].sort((a, b) => String(b.time || '').localeCompare(String(a.time || '')))
